
import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// ProductWise GST Report Display Component
const ProductWiseGSTReportDisplay = ({ formData, filters, branchProfile, purchaseData, productDetails }) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get product details from productDetails array
  const getProductDetails = (productName) => {
    if (!productDetails || !productName) return {};
    const product = productDetails.find(p => p.productName === productName);
    return product || {};
  };

  // Filter purchaseData by selected criteria
  let filteredPurchaseData = [...purchaseData];

  // Date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredPurchaseData = filteredPurchaseData.filter(item => {
      const itemDate = parseInvoiceDate(item.invoiceDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Supplier filter
  if (!filters.supplier.includes("All Suppliers")) {
    filteredPurchaseData = filteredPurchaseData.filter(item => 
      filters.supplier.includes(item.supplier)
    );
  }

  // Group filter
  if (!filters.group.includes("All Groups")) {
    filteredPurchaseData = filteredPurchaseData.filter(item => 
      filters.group.includes(item.groupName)
    );
  }

  // Product filter
  if (!filters.product.includes("All Products")) {
    filteredPurchaseData = filteredPurchaseData.filter(item => 
      filters.product.includes(item.productName)
    );
  }

  // Initialize displayData
  let displayData = [];

  // Process data based on report type
  if (formData.reportType === "abstract") {
    const groupedData = filteredPurchaseData.reduce((acc, item) => {
      const key = `${item.mrnNo || 'N/A'}-${item.invoiceNo || 'N/A'}-${item.productName}`;
      if (!acc[key]) {
        const productInfo = getProductDetails(item.productName);
        
        // Use actual GST rates and amounts from invoice data
        const cgstPercent = parseFloat(item.cgstPercent) || 0;
        const sgstPercent = parseFloat(item.sgstPercent) || 0;
        const igstPercent = parseFloat(item.igstPercent) || 0;
        const cessPercent = parseFloat(item.cessPercent) || 0;

        acc[key] = {
          mrnNo: item.mrnNo || "N/A",
          mrnDate: item.invoiceDate || "",
          invNo: item.invoiceNo || "N/A",
          invDate: item.invoiceDate || "",
          productName: item.productName || "",
          hsnCode: productInfo.hsnSacCode || item.hsnSacCode || item.hsnCode || "N/A",
          gstSlab: item.gstSlab || "0%",
          cgstPercent: cgstPercent,
          cgstAmt: 0,
          sgstPercent: sgstPercent,
          sgstAmt: 0,
          igstPercent: igstPercent,
          igstAmt: 0,
          cessPercent: cessPercent,
          cessAmt: 0,
          grossAmt: 0,
          netValue: 0,
          totalTaxableValue: 0
        };
      }
      
      // Accumulate actual amounts from invoice data
      acc[key].cgstAmt += parseFloat(item.cgstAmt) || 0;
      acc[key].sgstAmt += parseFloat(item.sgstAmt) || 0;
      acc[key].igstAmt += parseFloat(item.igstAmt) || 0;
      acc[key].cessAmt += parseFloat(item.cessAmt) || 0;
      acc[key].grossAmt += parseFloat(item.grossAmt) || 0;
      acc[key].netValue += parseFloat(item.netValue) || 0;
      acc[key].totalTaxableValue += parseFloat(item.taxableValue) || 0;
      
      return acc;
    }, {});
    
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => {
      const dateA = parseInvoiceDate(a.mrnDate);
      const dateB = parseInvoiceDate(b.mrnDate);
      return dateA - dateB || a.productName.localeCompare(b.productName);
    });
  } else {
    // Detail report - use actual data from invoice
    displayData = filteredPurchaseData.map(item => {
      const productInfo = getProductDetails(item.productName);
      
      return {
        ...item,
        mrnNo: item.mrnNo || "N/A",
        mrnDate: item.invoiceDate || "",
        invNo: item.invoiceNo || "N/A",
        invDate: item.invoiceDate || "",
        hsnCode: productInfo.hsnSacCode || item.hsnSacCode || item.hsnCode || "N/A",
        gstSlab: item.gstSlab || "0%",
        cgstPercent: parseFloat(item.cgstPercent) || 0,
        sgstPercent: parseFloat(item.sgstPercent) || 0,
        igstPercent: parseFloat(item.igstPercent) || 0,
        cessPercent: parseFloat(item.cessPercent) || 0,
        cgstAmt: parseFloat(item.cgstAmt) || 0,
        sgstAmt: parseFloat(item.sgstAmt) || 0,
        igstAmt: parseFloat(item.igstAmt) || 0,
        cessAmt: parseFloat(item.cessAmt) || 0,
        grossAmt: parseFloat(item.grossAmt) || 0,
        netValue: parseFloat(item.netValue) || 0
      };
    });
    
    displayData.sort((a, b) => {
      return a.productName.localeCompare(b.productName) || parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
    });
  }

  // Calculate totals
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || 0), 0);
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0);
  const totalGstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.gstAmt) || (parseFloat(item.cgstAmt) + parseFloat(item.sgstAmt) + parseFloat(item.igstAmt)) || 0), 0);
  const totalCessAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cessAmt) || 0), 0);
  const totalGrossAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.grossAmt) || 0), 0);
  const totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || 0), 0);

  const city = branchProfile.city || (branchProfile.address ? branchProfile.address.split(/\s+/).pop() : 'Unknown City');

  const renderTableHeaders = () => {
    return (
      <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRN No</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRN Date</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv. No</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv. Date</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>HSN Code</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST SLAB</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST %</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST AMT</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST %</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST AMT</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>IGST %</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>IGST AMT</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST AMT</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS %</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS AMT</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt</th>
        <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Amount</th>
      </tr>
    );
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      return (
        <tr>
          <td colSpan={19} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No purchase data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => (
      <tr key={`${item.mrnNo}-${item.invNo}-${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.mrnNo}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.mrnDate || item.invoiceDate)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invNo || item.invoiceNo || 'N/A'}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.invDate || item.invoiceDate)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.hsnCode}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.gstSlab}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{parseFloat(item.cgstPercent).toFixed(2)}%</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.cgstAmt).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{parseFloat(item.sgstPercent).toFixed(2)}%</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.sgstAmt).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{parseFloat(item.igstPercent).toFixed(2)}%</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.igstAmt).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.gstAmt || (parseFloat(item.cgstAmt) + parseFloat(item.sgstAmt) + parseFloat(item.igstAmt))).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{parseFloat(item.cessPercent).toFixed(2)}%</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.cessAmt).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.grossAmt).toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{parseFloat(item.netValue).toFixed(2)}</td>
      </tr>
    ));
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;

    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan={9} style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalIgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  };

  const renderGrandTotalRow = () => {
    if (displayData.length === 0) return null;

    return (
      <tr style={{ backgroundColor: '#fee2e2', fontWeight: 'bold', borderTop: '2px solid #dc2626' }}>
        <td colSpan={9} style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalIgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}></td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="report-display">
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          margin: '0',
          marginBottom: '5px',
          color: '#1e293b',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        }}>
          {branchProfile.branchName}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>{city}</div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>
          Productwise Purchase Annexure Report From : 
          {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'N/A'} To : 
          {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'N/A'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
        </div>
      </div>

      {/* Purchase Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
            {renderGrandTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#0891b2' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#c2410c' }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CESS</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalGrossAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const PurchaseProductWiseGST = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("Productwise");
  const [reportType, setReportType] = useState("detail");
  const [filters, setFilters] = useState({
    supplier: ["All Suppliers"],
    group: ["All Groups"],
    product: ["All Products"]
  });
  const [suppliers, setSuppliers] = useState(["All Suppliers"]);
  const [groups, setGroups] = useState(["All Groups"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [purchaseData, setPurchaseData] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = async (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || (latestProfile.address1 ? latestProfile.address1.split(/\s+/).pop() : 'Unknown City')
        });
      }
    });

    // Load Suppliers
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Suppliers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
          }
        });
      }
      setSuppliers(suppliersData);
    });

    // Load Groups first and create mapping
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"];
      const groupsMapping = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
            groupsMapping[childSnapshot.key] = group.name;
            groupsMapping[group.name] = group.name;
          }
        });
      }
      setGroups(groupsData);
      
      // After groups are loaded, load products
      loadProductsWithGroups(encodedEmail, groupsMapping);
    });

    return () => {
      off(branchProfileRef);
      off(suppliersRef);
      off(groupsRef);
    };
  };

  const loadProductsWithGroups = (encodedEmail, groupsMapping) => {
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      const productDetailsArray = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            
            let groupName = 'No Group Assigned';
            if (product.groupName && product.groupName !== '') {
              groupName = product.groupName;
            } else if (product.group && groupsMapping[product.group]) {
              groupName = groupsMapping[product.group];
            } else if (product.group && Object.values(groupsMapping).includes(product.group)) {
              groupName = product.group;
            }
            
            groupMap[product.productName] = groupName;
            
            // Store complete product details with hsnSacCode
            productDetailsArray.push({
              productName: product.productName,
              hsnSacCode: product.hsnSacCode || product.hsnCode || product.hsn || "",
              hsnCode: product.hsnSacCode || product.hsnCode || product.hsn || "",
              gstRate: product.gstRate || product.gst || 0,
              igstRate: product.igstRate || 0,
              cessRate: product.cessRate || 0,
              groupName: groupName
            });
          }
        });
      }
      
      setProducts(productsData);
      setProductGroupMap(groupMap);
      setProductDetails(productDetailsArray);
      
      loadInvoicesWithGroupMapping(encodedEmail, groupMap);
    });
  };

const loadInvoicesWithGroupMapping = (encodedEmail, groupMap) => {
  const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
  onValue(invoicesRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const invoiceRecords = [];
      
      Object.entries(data).forEach(([invoiceId, invoice]) => {
        if (invoice.items && Array.isArray(invoice.items) && invoice.supplierDetails) {
          // Check if IGST is applied for this invoice
          const isIgstApplied = invoice.summary?.igstAmt > 0;
          
          invoice.items.forEach(item => {
            if (item.item && item.qty > 0) {
              const qty = parseFloat(item.qty) || 0;
              const pRate = parseFloat(item.pRate) || 0;
              const grossAmt = qty * pRate;

              // Discount
              const discPercent = parseFloat(item.discPercent) || 0;
              const discAmt = parseFloat(item.discAmt) || (grossAmt * discPercent / 100) || 0;
              const taxableValue = grossAmt - discAmt;

              // Use actual GST percentages and amounts from invoice data
              const cgstPercent = parseFloat(item.cgstPercent) || 0;
              const sgstPercent = parseFloat(item.sgstPercent) || 0;
              const igstPercent = parseFloat(item.igstPercent) || 0;
              const cessPercent = parseFloat(item.cessPercent) || 0;

              // Use actual GST amounts from invoice data
              const cgstAmt = parseFloat(item.cgstAmt) || 0;
              const sgstAmt = parseFloat(item.sgstAmt) || 0;
              const igstAmt = parseFloat(item.igstAmt) || 0;
              const cessAmt = parseFloat(item.cessAmt) || 0;

              // Calculate total GST amount
              const gstAmt = cgstAmt + sgstAmt + igstAmt;

              // Calculate GST slab from actual rates
              const totalGstRate = cgstPercent + sgstPercent + igstPercent;
              const gstSlab = totalGstRate > 0 ? `${totalGstRate.toFixed(2)}%` : "0%";

              const groupName = groupMap[item.item] || 'No Group Assigned';

              invoiceRecords.push({
                invoiceId,
                mrnNo: invoice.supplierDetails.mrnNo || 'N/A',
                mrnDate: invoice.supplierDetails.invoiceDate || "",
                invoiceNo: invoice.supplierDetails.invoiceNo || 'N/A',
                invNo: invoice.supplierDetails.invoiceNo || 'N/A',
                invoiceDate: invoice.supplierDetails.invoiceDate || "",
                invDate: invoice.supplierDetails.invoiceDate || "",
                supplier: invoice.supplierDetails.supplier || "Unknown Supplier",
                gstin: invoice.supplierDetails.gstin || "N/A",
                productName: item.item,
                groupName,
                batch: item.batch || "",
                eDate: item.eDate || "",
                qty,
                pRate,
                grossAmt,
                discPercent,
                discAmt,
                taxableValue,
                gstSlab,           // GST SLAB
                cgstPercent,       // CGST %
                cgstAmt,           // CGST AMT
                sgstPercent,       // SGST %
                sgstAmt,           // SGST AMT
                igstPercent,       // IGST %
                igstAmt,           // IGST AMT
                gstAmt,            // GST AMT (total of CGST + SGST + IGST)
                cessPercent,       // CESS %
                cessAmt,           // CESS AMT
                netValue: parseFloat(item.netValue) || (taxableValue + gstAmt + cessAmt),
                hsnCode: item.hsnSacCode || item.hsnCode || "",
                isIgstApplied      // Flag to track IGST application
              });
            }
          });
        }
      });
      
      setPurchaseData(invoiceRecords);
    } else {
      setPurchaseData([]);
    }
  });
};


  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const handlePrint = () => {
    const printContent = (
      <ProductWiseGSTReportDisplay 
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        purchaseData={purchaseData}
        productDetails={productDetails}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Productwise GST Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 2px !important;
              font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths for better fit */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 50px; } /* MRN No */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* MRN Date */
            th:nth-child(4), td:nth-child(4) { width: 50px; } /* Inv. No */
            th:nth-child(5), td:nth-child(5) { width: 50px; } /* Inv. Date */
            th:nth-child(6), td:nth-child(6) { width: 80px; } /* Product Name */
            th:nth-child(7), td:nth-child(7) { width: 50px; } /* HSN Code */
            th:nth-child(8), td:nth-child(8) { width: 40px; } /* GST SLAB */
            th:nth-child(9), td:nth-child(9) { width: 40px; } /* CGST % */
            th:nth-child(10), td:nth-child(10) { width: 50px; } /* CGST AMT */
            th:nth-child(11), td:nth-child(11) { width: 40px; } /* SGST % */
            th:nth-child(12), td:nth-child(12) { width: 50px; } /* SGST AMT */
            th:nth-child(13), td:nth-child(13) { width: 40px; } /* IGST % */
            th:nth-child(14), td:nth-child(14) { width: 50px; } /* IGST AMT */
            th:nth-child(15), td:nth-child(15) { width: 50px; } /* GST AMT */
            th:nth-child(16), td:nth-child(16) { width: 40px; } /* CESS % */
            th:nth-child(17), td:nth-child(17) { width: 45px; } /* CESS AMT */
            th:nth-child(18), td:nth-child(18) { width: 50px; } /* Gross Amt */
            th:nth-child(19), td:nth-child(19) { width: 60px; font-weight: bold; } /* Net Amount */
            
            .print-only {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Purchase Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Productwise GST Report Generator</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Productwise (HSN)_1A</h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="Productwise">Productwise</option>
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="detail">Detail</option>
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <FilterSection 
          title="Supplier Name" 
          options={suppliers} 
          selected={filters.supplier} 
          onChange={(value) => handleFilterChange("supplier", value)}
        />
        <FilterSection 
          title="Group Name" 
          options={groups} 
          selected={filters.group} 
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection 
          title="Product Name" 
          options={products} 
          selected={filters.product} 
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <ProductWiseGSTReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseData={purchaseData}
          productDetails={productDetails}
        />
      )}
    </div>
  );
};

export default PurchaseProductWiseGST;